import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import { PageHeader } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DecimalInput } from "@/components/ui/decimal-input";
import { SourceOfTruthBadge } from "@/components/ledger/SourceOfTruthBadge";
import { usePeople } from "@/lib/people-store";
import { isKarigarParty } from "@/lib/party-types";
import { useMaterialVault } from "@/lib/material-vault-store";
import { useLedger } from "@/lib/ledger-store";
import { formatWeight, mgToGrams, gramsToMg } from "@/lib/gold";
import { toast } from "sonner";
import {
  Sparkles,
  Layers,
  Scale,
  CheckCircle2,
  History,
  ArrowRight,
  RefreshCw,
  Archive,
} from "lucide-react";

export const Route = createFileRoute("/workshop/vibrator")({
  head: () => ({ meta: [{ title: "Recovery & Vibrator Workstation · AVS ERP" }] }),
  component: VibratorRecoveryPage,
});

export function VibratorRecoveryPage() {
  const people = usePeople((s) => s.people);
  const karigars = useMemo(
    () => people.filter((p) => isKarigarParty(p) && p.active),
    [people],
  );

  const { movements: vaultItems, refresh: refreshVault, append: appendVault } = useMaterialVault();
  const appendLedger = useLedger((s) => s.append);

  useEffect(() => {
    void refreshVault();
  }, [refreshVault]);

  // Form State
  const [processKind, setProcessKind] = useState<
    "vibrator_tumbling" | "bench_sweepings" | "polishing_dust" | "ultrasonic_wash"
  >("vibrator_tumbling");
  const [batchRef, setBatchRef] = useState(`REC-${new Date().toISOString().slice(2, 10).replace(/-/g, "")}`);
  const [selectedKarigarId, setSelectedKarigarId] = useState<string>("");
  const [grossGrams, setGrossGrams] = useState("");
  const [purityPermille, setPurityPermille] = useState("916");
  const [targetVault, setTargetVault] = useState<"scrap" | "raw">("scrap");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Calculations
  const calc = useMemo(() => {
    const gross = parseFloat(grossGrams) || 0;
    const purity = parseInt(purityPermille, 10) || 0;
    const grossMg = gramsToMg(gross);
    const fine995Mg = purity > 0 ? Math.round((grossMg * purity) / 995) : 0;
    return {
      grossMg,
      fine995Mg,
      fine995G: mgToGrams(fine995Mg),
    };
  }, [grossGrams, purityPermille]);

  async function handleDeposit(e: React.FormEvent) {
    e.preventDefault();
    if (calc.grossMg <= 0) {
      toast.error("Please enter a valid recovered weight.");
      return;
    }

    setSubmitting(true);
    try {
      const karigar = karigars.find((k) => k.id === selectedKarigarId);
      const karigarInfo = karigar ? ` (${karigar.fullName})` : "";
      const description = `Recovery: ${processKind.replace("_", " ")}${karigarInfo} · Ref: ${batchRef}`;

      // 1. Post to Material Vault (Scrap or Raw)
      await appendVault({
        category: targetVault === "scrap" ? "scrap" : "raw_gold",
        type: "worker_return",
        deltaMg: calc.grossMg,
        grossMg: calc.grossMg,
        purity: parseInt(purityPermille, 10) || 916,
        reference: batchRef,
        remarks: notes.trim() || description,
      });

      // 2. Post to Gold Ledger
      await appendLedger({
        type: "dust_returned",
        netFineMg: calc.fine995Mg,
        deltas: { vault: calc.fine995Mg },
        grossMg: calc.grossMg,
        purity: parseInt(purityPermille, 10) || 916,
        fineMg: calc.fine995Mg,
        reference: batchRef,
        notes: notes.trim() || description,
      });

      toast.success(
        `Successfully recovered ${formatWeight(calc.grossMg)} (${formatWeight(calc.fine995Mg)} @995) into Vault!`,
      );
      setGrossGrams("");
      setNotes("");
      setBatchRef(`REC-${new Date().toISOString().slice(2, 10).replace(/-/g, "")}-${Math.floor(Math.random() * 90 + 10)}`);
      await refreshVault();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to record recovery.");
    } finally {
      setSubmitting(false);
    }
  }

  // Filter recent recovery events
  const recentRecoveries = useMemo(() => {
    return vaultItems
      .filter((v: any) => v.remarks?.toLowerCase().includes("recovery") || v.reference?.startsWith("REC-"))
      .slice(0, 15);
  }, [vaultItems]);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
      <PageHeader
        title="Recovery & Vibrator Workstation"
        subtitle="Collect and process tumbling recoveries, bench filings, polishing dust, and ultrasonic sweepings directly into the vault."
        actions={<SourceOfTruthBadge variant="operational" />}
      />

      {/* Top Banner */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl border border-border bg-card">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-amber-500" />
          <div>
            <h2 className="text-sm font-semibold text-foreground">SRM Metal Recovery Lifecycle</h2>
            <p className="text-xs text-muted-foreground">
              Recovered metal directly offsets manufacturing loss and restores fine gold @995 into vault reserves.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/workshop/jama">
              Jama Return Form
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/workshop/reconciliation">
              Reconciliation
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recovery Form */}
        <form
          onSubmit={handleDeposit}
          className="lg:col-span-2 rounded-xl border border-border bg-card p-5 space-y-5 shadow-xs"
        >
          <div className="border-b border-border pb-3 flex items-center justify-between">
            <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
              <Archive className="h-4 w-4 text-amber-500" />
              Record Dust / Vibrator Recovery
            </h3>
            <span className="text-xs text-muted-foreground">Vault Inward</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Recovery Process Type *</Label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={processKind}
                onChange={(e) => setProcessKind(e.target.value as any)}
              >
                <option value="vibrator_tumbling">Vibrator Tumbling Collection</option>
                <option value="polishing_dust">Polishing Dust / Vacuum Filter</option>
                <option value="bench_sweepings">Artisan Bench Sweepings</option>
                <option value="ultrasonic_wash">Ultrasonic Tank Wash Sediment</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Batch / Slip Reference</Label>
              <Input
                value={batchRef}
                onChange={(e) => setBatchRef(e.target.value)}
                className="font-mono text-sm"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Associated Artisan / Bench (Optional)</Label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={selectedKarigarId}
                onChange={(e) => setSelectedKarigarId(e.target.value)}
              >
                <option value="">-- General Workshop Batch --</option>
                {karigars.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.fullName}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Destination Vault Bucket</Label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={targetVault}
                onChange={(e) => setTargetVault(e.target.value as any)}
              >
                <option value="scrap">Scrap / Melt Vault (Recommended)</option>
                <option value="raw">Raw Bullion Vault</option>
              </select>
            </div>
          </div>

          {/* Recovery Weights */}
          <div className="p-4 rounded-lg border border-border bg-muted/20 space-y-3">
            <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider">
              Weight & Estimated Touch
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs">Recovered Gross Wt (g) *</Label>
                <DecimalInput
                  placeholder="0.000"
                  value={grossGrams}
                  onChange={setGrossGrams}
                  className="font-mono"
                  required
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Estimated Touch Purity (‰)</Label>
                <Input
                  placeholder="916"
                  value={purityPermille}
                  onChange={(e) => setPurityPermille(e.target.value)}
                  className="font-mono"
                />
                <span className="text-[11px] text-muted-foreground">
                  Benchmark 995 Fine Gold will be credited to Vault
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Notes / Recovery Particulars</Label>
            <Input
              placeholder="e.g. Weekly tumbling bowl cleanout, bench sweepings lot 12..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="pt-2 flex justify-end">
            <Button
              type="submit"
              variant="default"
              className="px-6 cursor-pointer bg-amber-500 hover:bg-amber-600 text-black font-semibold"
              disabled={submitting}
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              {submitting ? "Depositing..." : "Deposit Recovery to Vault"}
            </Button>
          </div>
        </form>

        {/* Right Info Box */}
        <div className="space-y-6">
          <div className="rounded-xl border border-border bg-card p-5 space-y-4 shadow-xs">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Scale className="h-4 w-4 text-amber-500" />
              Recovery Inward Calculation
            </h3>

            <div className="space-y-2.5 text-xs">
              <div className="flex justify-between py-1.5 border-b border-border/60">
                <span className="text-muted-foreground">Recovered Physical Weight:</span>
                <span className="font-mono font-medium">{formatWeight(calc.grossMg)}</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-border/60">
                <span className="text-muted-foreground">Purity / Touch:</span>
                <span className="font-mono font-medium">{purityPermille}‰</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-border/60">
                <span className="text-muted-foreground">Fine Gold Credited @995:</span>
                <span className="font-mono font-semibold text-amber-500">
                  {formatWeight(calc.fine995Mg)}
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-border/60">
                <span className="text-muted-foreground">Target Destination:</span>
                <span className="font-medium capitalize">{targetVault} Vault</span>
              </div>
            </div>
          </div>

          {/* Recent Recoveries */}
          <div className="rounded-xl border border-border bg-card p-5 space-y-3 shadow-xs">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <History className="h-4 w-4 text-primary" />
              Recent Vault Recoveries
            </h3>

            {recentRecoveries.length === 0 ? (
              <p className="text-xs text-muted-foreground">No recent recovery receipts found.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {recentRecoveries.map((r: any) => (
                  <div
                    key={r.id}
                    className="p-2.5 rounded-md border border-border/60 bg-muted/20 text-xs flex justify-between items-center"
                  >
                    <div>
                      <div className="font-semibold text-foreground">{r.reference || "REC"}</div>
                      <div className="text-muted-foreground">{r.remarks || r.category}</div>
                    </div>
                    <div className="text-right font-mono">
                      <div>+{formatWeight(r.grossMg || r.deltaMg)}</div>
                      <div className="text-amber-500 text-[11px]">{r.category}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
